import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, ChevronRight, MapPin, Loader2 } from "lucide-react";
import { ServiceSchema, FAQSchema, BreadcrumbSchema, SpeakableSchema, setCanonical, setMetaTags } from "@/components/seo/SchemaMarkup";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAJINI_URL = "https://storajini.com";

const TYPE_LABELS = { storage: "Storage", parking: "Parking", moving: "Moving" };
const TYPE_COLORS = { storage: "blue", parking: "emerald", moving: "amber" };

export default function CityPage({ type }) {
  const { slug } = useParams();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/content/${type}/${slug}`)
      .then(r => {
        setContent(r.data);
        setCanonical(`/${type}/${slug}`);
        setMetaTags({ title: r.data.meta_title, description: r.data.meta_description });
      })
      .catch(() => setContent(null))
      .finally(() => setLoading(false));
  }, [type, slug]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-amber-600 animate-spin" />
      </div>
    );
  }

  if (!content) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
        <h1 className="text-2xl font-bold text-gray-900 mb-3">City Not Found</h1>
        <p className="text-gray-500 mb-6">We couldn't find information for this city.</p>
        <Link to="/" className="text-amber-600 font-semibold hover:underline">Back to Home</Link>
      </div>
    );
  }

  const typeLabel = TYPE_LABELS[type];
  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: typeLabel, url: `/${type}/manila` },
    { name: content.city.name },
  ];

  return (
    <div data-testid="city-page">
      <ServiceSchema cityName={content.city.name} serviceType={typeLabel} description={content.meta_description} />
      <FAQSchema faqs={content.faqs} />
      <BreadcrumbSchema items={breadcrumbs} />
      <SpeakableSchema selectors={["h1", ".speakable-intro"]} />

      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b border-gray-100">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-3">
          <nav className="flex items-center gap-2 text-sm text-gray-500" data-testid="breadcrumb">
            {breadcrumbs.map((item, i) => (
              <span key={i} className="flex items-center gap-2">
                {i > 0 && <ChevronRight className="w-3.5 h-3.5" />}
                {item.url ? (
                  <Link to={item.url} className="hover:text-amber-600">{item.name}</Link>
                ) : (
                  <span className="text-gray-900 font-medium">{item.name}</span>
                )}
              </span>
            ))}
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-amber-900 py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="w-5 h-5 text-amber-400" />
              <span className="text-amber-300 font-medium">{content.city.region}, Philippines</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-4 leading-tight">
              {content.h1}
            </h1>
            <p className="speakable-intro text-lg text-gray-300 leading-relaxed mb-8">{content.intro}</p>
            <a
              href={`${STORAJINI_URL}/search`}
             
             
              data-testid="city-hero-cta"
              className="inline-flex items-center px-7 py-3.5 bg-amber-500 text-white font-bold rounded-lg hover:bg-amber-600 transition-all"
            >
              {content.cta_text} <ArrowRight className="ml-2 w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Tips */}
      <section className="py-14 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">{typeLabel} Tips for {content.city.name}</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {content.tips.map((tip, i) => (
              <div key={i} className="p-6 bg-gray-50 rounded-xl border border-gray-100" data-testid={`tip-${i}`}>
                <h3 className="font-semibold text-gray-900 mb-2">{tip.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{tip.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA - Send to Storajini */}
      <section className="py-14 bg-gray-50">
        <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Need Help Finding Space?</h2>
            <p className="text-gray-500 mb-6">Browse verified storage and parking spaces in {content.city.name} and across the Philippines on Storajini.com.</p>
            <a
              href={`${STORAJINI_URL}/search`}
              data-testid="city-get-quotes-btn"
              className="inline-flex items-center px-8 py-4 bg-amber-500 text-white font-bold rounded-lg hover:bg-amber-600 transition-colors text-lg"
            >
              Get Free Quotes <ArrowRight className="ml-2 w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 bg-white">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {content.faqs.map((faq, i) => (
              <details key={i} className="group bg-gray-50 rounded-xl border border-gray-100" data-testid={`city-faq-${i}`}>
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer list-none">
                  <span className="font-semibold text-gray-900 text-sm pr-4">{faq.question}</span>
                  <ChevronRight className="w-5 h-5 text-gray-400 group-open:rotate-90 transition-transform flex-shrink-0" />
                </summary>
                <div className="px-6 pb-4">
                  <p className="text-gray-500 text-sm leading-relaxed">{faq.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Nearby Cities */}
      {content.nearby_cities?.length > 0 && (
        <section className="py-14 bg-gray-50">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Explore Nearby Cities</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
              {content.nearby_cities.map((city) => (
                <Link
                  key={city.slug}
                  to={`/${type}/${city.slug}`}
                  data-testid={`nearby-city-${city.slug}`}
                  className="group p-4 bg-white rounded-xl border border-gray-100 hover:border-amber-200 hover:bg-amber-50 transition-all text-center"
                >
                  <p className="font-semibold text-gray-900 group-hover:text-amber-600 text-sm">{city.name}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Cross-link other services */}
      <section className="py-14 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">More in {content.city.name}</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {["storage", "parking", "moving"].filter(t => t !== type).map((t) => (
              <Link key={t} to={`/${t}/${slug}`} data-testid={`cross-link-${t}`} className="group flex items-center justify-between p-5 bg-gray-50 rounded-xl border border-gray-100 hover:border-amber-200 hover:bg-amber-50 transition-all">
                <div>
                  <p className="font-semibold text-gray-900 group-hover:text-amber-600">{TYPE_LABELS[t]} in {content.city.name}</p>
                  <p className="text-sm text-gray-500 mt-1">Explore {t} options</p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-amber-600" />
              </Link>
            ))}
            <a href={`${STORAJINI_URL}/search`} className="group flex items-center justify-between p-5 bg-amber-50 rounded-xl border border-amber-100 hover:border-amber-300 transition-all">
              <div>
                <p className="font-semibold text-amber-700">Browse on Storajini.com</p>
                <p className="text-sm text-amber-500 mt-1">Find actual spaces to rent</p>
              </div>
              <ArrowRight className="w-5 h-5 text-amber-500" />
            </a>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-14 bg-amber-500">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">{content.cta_text}</h2>
          <p className="text-amber-100 mb-6">Browse verified spaces and book directly on Storajini.com</p>
          <a
            href={`${STORAJINI_URL}/search`}
           
           
            data-testid="city-bottom-cta"
            className="inline-flex items-center px-8 py-4 bg-white text-amber-600 font-bold rounded-lg hover:bg-gray-50 transition-colors"
          >
            Browse Spaces on Storajini <ArrowRight className="ml-2 w-5 h-5" />
          </a>
        </div>
      </section>
    </div>
  );
}
